import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/authContext';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { XMarkIcon, ArchiveBoxIcon, InboxIcon } from '@heroicons/react/24/outline';

const OrgMessages = ({ organizationId }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [archivedMessages, setArchivedMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedIds, setArchivedIds] = useState(new Set());

  const archiveMessage = async (messageId, orgId) => {
    try {
      const archiveRef = doc(db, 'users', currentUser.uid, 'archivedMessages', `${orgId}_${messageId}`);
      
      // Find the message we're archiving and make sure we have all the data
      const messageToArchive = messages.find(msg => msg.id === messageId && msg.orgId === orgId);
      if (!messageToArchive) {
        console.error('Message not found for archiving');
        return;
      }

      // Store the archive record with timestamp and full message data
      await setDoc(archiveRef, {
        messageId,
        orgId,
        archivedAt: new Date(),
        // Store all the message data we need
        message: {
          ...messageToArchive,
          createdAt: messageToArchive.createdAt?.toDate?.() || new Date(),
        }
      });

      // Update local state
      setArchivedIds(prev => new Set([...prev, `${orgId}_${messageId}`]));
      setMessages(prev => prev.filter(msg => !(msg.id === messageId && msg.orgId === orgId)));
      setArchivedMessages(prev => [...prev, messageToArchive]);

    } catch (error) {
      console.error('Error archiving message:', error);
    }
  };

  // Add function to unarchive message
  const unarchiveMessage = async (messageId, orgId) => {
    try {
      const archiveRef = doc(db, 'users', currentUser.uid, 'archivedMessages', `${orgId}_${messageId}`);
      await deleteDoc(archiveRef);

      // Update local state
      setArchivedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(`${orgId}_${messageId}`);
        return newSet;
      });

      // Move message back to active messages
      const messageToUnarchive = archivedMessages.find(
        msg => `${msg.orgId}_${msg.id}` === `${orgId}_${messageId}`
      );
      if (messageToUnarchive) {
        setMessages(prev => [...prev, messageToUnarchive].sort(
          (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)
        ));
        setArchivedMessages(prev => prev.filter(
          msg => `${msg.orgId}_${msg.id}` !== `${orgId}_${messageId}`
        ));
      }
    } catch (error) {
      console.error('Error unarchiving message:', error);
    }
  };

  useEffect(() => {
    const fetchMessages = async () => {
      if (!currentUser?.uid || !organizationId) {
        setLoading(false);
        return;
      }

      try {
        // First fetch archived messages
        const archivedRef = collection(db, 'users', currentUser.uid, 'archivedMessages');
        const archivedSnap = await getDocs(archivedRef);
        const archived = new Set(archivedSnap.docs.map(doc => doc.id));
        setArchivedIds(archived);

        // Get archived messages data with proper structure - filtered for this org
        const archivedMessagesData = archivedSnap.docs
          .map(doc => {
            const data = doc.data();
            // Make sure we have the message data and it's from the selected org
            if (data.message && data.orgId === organizationId) {
              return {
                ...data.message,
                createdAt: data.message.createdAt?.toDate?.() || new Date(data.message.createdAt) || new Date(),
              };
            }
            return null;
          })
          .filter(Boolean); // Remove any null values

        console.log('Archived messages for org:', archivedMessagesData);

        // Get only the selected organization's data and messages
        const orgRef = doc(db, 'organizations', organizationId);
        const orgSnap = await getDoc(orgRef);
        const orgData = orgSnap.data();

        const activeMessages = [];

        if (orgData) {
          const messagesQuery = query(
            collection(db, 'organizations', organizationId, 'messages'),
            orderBy('createdAt', 'desc')
          );

          const messagesSnap = await getDocs(messagesQuery);
          const orgMessages = messagesSnap.docs
            .filter(doc => !archived.has(`${organizationId}_${doc.id}`) && doc.data().archived !== true)
            .map(doc => ({
              id: doc.id,
              orgId: organizationId,
              orgName: orgData.name || 'Unknown Organization',
              ...doc.data(),
              createdAt: doc.data().createdAt?.toDate() || new Date()
            }));

          activeMessages.push(...orgMessages);
        }

        setMessages(activeMessages.sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        ));
        
        setArchivedMessages(archivedMessagesData.sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
        ));

      } catch (error) {
        console.error('Error in OrgMessages:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [currentUser, organizationId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="flex justify-between items-center mb-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/5"></div>
          </div>
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            Announcements
          </h2>
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {showArchived ? (
              <>
                <InboxIcon className="h-5 w-5" />
                View Active
              </>
            ) : (
              <>
                <ArchiveBoxIcon className="h-5 w-5" />
                View Archived ({archivedMessages.length})
              </>
            )}
          </button>
        </div>

        {showArchived ? (
          archivedMessages.length > 0 ? (
            <div className="space-y-4">
              {archivedMessages.map((message) => (
                <div 
                  key={`${message.orgId}_${message.id}`}
                  className="relative border border-gray-200 rounded-lg p-3 bg-gray-50"
                >
                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {message.title || 'Organization Update'}
                  </h3>

                  {/* Message Text */}
                  <div className="mb-2">
                    <p className="text-sm text-gray-700 leading-snug">{message.text}</p>
                  </div>

                  {/* Image if present */}
                  {message.imageUrl && (
                    <div className="mb-2">
                      <img 
                        src={message.imageUrl} 
                        alt={message.title || 'Announcement image'}
                        className="w-full max-w-md h-auto rounded-md shadow-sm"
                      />
                    </div>
                  )}

                  {/* Link if present */}
                  {message.link && (
                    <a 
                      href={message.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-[#5ABA47] text-white text-sm px-4 py-1.5 rounded-md hover:bg-[#4a9a3a] transition-colors mb-2"
                    >
                      {message.linkText || 'Learn More →'}
                    </a>
                  )}

                  {/* Org name and date on same line */}
                  <div className="flex justify-between items-center text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
                    <span>From {message.orgName}</span>
                    <span>{message.createdAt?.toLocaleDateString()}</span>
                  </div>

                  {/* Unarchive Button */}
                  <button
                    onClick={() => unarchiveMessage(message.id, message.orgId)}
                    className="mt-2 w-full text-sm text-gray-600 hover:text-gray-800 py-1.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Restore Message
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6">
              No archived messages
            </p>
          )
        ) : (
          messages.length > 0 ? (
            <div className="space-y-4">
              {messages.map((message) => (
                <div 
                  key={`${message.orgId}_${message.id}`}
                  className="relative border border-gray-200 rounded-lg p-3 bg-white shadow-sm hover:shadow-md transition-shadow"
                >
                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {message.title || 'Organization Update'}
                  </h3>

                  {/* Message Text */}
                  <div className="mb-2">
                    <p className="text-sm text-gray-700 leading-snug">{message.text}</p>
                  </div>

                  {/* Image if present */}
                  {message.imageUrl && (
                    <div className="mb-2">
                      <img 
                        src={message.imageUrl} 
                        alt={message.title || 'Announcement image'}
                        className="w-full max-w-md h-auto rounded-md shadow-sm"
                      />
                    </div>
                  )}

                  {/* Link if present */}
                  {message.link && (
                    <a 
                      href={message.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-[#5ABA47] text-white text-sm px-4 py-1.5 rounded-md hover:bg-[#4a9a3a] transition-colors mb-2"
                    >
                      {message.linkText || 'Learn More →'}
                    </a>
                  )}

                  {/* Org name and date on same line */}
                  <div className="flex justify-between items-center text-xs text-gray-400 mt-2 pt-2 border-t border-gray-200">
                    <span>From {message.orgName}</span>
                    <span>{message.createdAt?.toLocaleDateString()}</span>
                  </div>

                  {/* "I read it" Button */}
                  <button
                    onClick={() => archiveMessage(message.id, message.orgId)}
                    className="mt-2 w-full text-sm text-gray-600 hover:text-gray-800 py-1.5 px-4 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    I read it
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-6">
              No new messages from your organizations
            </p>
          )
        )}
      </div>
    </div>
  );
};

export default OrgMessages; 